/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { ComponentType, NoopScrollStrategy } from '@angular/cdk/overlay';
import { Injectable, TemplateRef } from '@angular/core';
import { MatDialog,  MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';
import { ErrorModalComponent } from './error-modal/error-modal.component';
import { ConfirmCancelModalComponent } from './confirmation-modal/confirm-cancel-modal.component';
import { map } from 'rxjs/operators';
import { DeleteDialogComponent } from './delete-dialog/delete-dialog.component';
import { DeleteDialogContent } from './delete-dialog/+model/delete-dialog-content';
import { ConfirmCancelModalContent } from './confirmation-modal/+model/confirm-cancel-modal-content';
import { ErrorModalContent } from './error-modal/+model/error-modal-content';
import { DetailDialogContent } from './detail-dialog/+model/detail-dialog-content';
import { DetailDialogComponent } from './detail-dialog/detail-dialog.component';
import { BaseModal } from '../+legacy/modals/base-modal.class';
import { ModalOptions } from '../+legacy/modals/modal.model';
import { ReloadAppDialogComponent } from './reload-app-dialog/reload-app-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class DialogService extends MatDialog {

  // TODO: remove after .show is removed (all internal app custom modals converted)
  public static readonly CANCEL_MODAL = {reason: 'CANCEL_MODAL'};

  open<T, D = any, R = any>(componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
                            config: MatDialogConfig<D> = {}): MatDialogRef<T, R> {
    config = {
      ...config,
      hasBackdrop: true,
      backdropClass: 'ngf-dialog-backdrop',
      panelClass: this.addPanelClass(config.panelClass, 'ngf-dialog'),
      minWidth: config.minWidth || 'min(500px, 95vw)',
    };

    return super.open(componentOrTemplateRef, config);
  }

  /**
   *
   * @param data
   * @param disableClose, default false. Determines whether or not the modal should allow for
   * closing on escape. Since error should have no action other than to acknowledge,
   * it is fine to allow for closing with the escape key.
   * @returns
   */
  openErrorDialog(data: ErrorModalContent, disableClose = false): MatDialogRef<ErrorModalComponent, never> {
    return this.open(ErrorModalComponent, {
      disableClose: disableClose,
      data: data,
      maxWidth: 'min(600px, 95vw)',
      scrollStrategy: new NoopScrollStrategy()
    });
  }

  openDetailDialog(data: DetailDialogContent,
    config?: { maxWidth?: string, panelClass?: string }): MatDialogRef<DetailDialogComponent, never> {
    return this.open(DetailDialogComponent, {
      disableClose: false,
      data: data,
      maxWidth: config?.maxWidth || 'min(600px, 95vw)',
      panelClass: config?.panelClass
    });
  }


  openConfirmCancelDialog(data: ConfirmCancelModalContent,
                         config?: { disableClose?: boolean, maxWidth?: string, minWidth?: string, panelClass?: string }
                         ): MatDialogRef<ConfirmCancelModalComponent, boolean> {
    return this.open(ConfirmCancelModalComponent, {
      disableClose: (config?.disableClose !== undefined) ? config.disableClose : false,
      data: data,
      maxWidth: config?.maxWidth || 'min(600px, 95vw)',
      minWidth: config?.minWidth || 'min(500px, 95vw)',
      panelClass: config?.panelClass
    });
  }

  openDeleteDialog(data: DeleteDialogContent,
    disableClose?: boolean): MatDialogRef<DeleteDialogComponent, boolean> {
    return this.open(DeleteDialogComponent, {
      disableClose: (disableClose !== undefined) ? disableClose : true,
      data: data,
      maxWidth: 'min(600px, 95vw)'
    });
  }

  openReloadAppDialog() {
    return this.open(ReloadAppDialogComponent, {
      disableClose: true,
      maxWidth: 'min(600px, 95vw)'
    });
  }

  private addPanelClass(panelClass: string | string[] | undefined, className: string): string[] {
    if (!panelClass) {
      return [className];
    }

    if (Array.isArray(panelClass)) {
      return [...panelClass, className];
    } else {
      return [panelClass, className];
    }
  }

  /**
    * @deprecated only here for backwards compatibility; please use .open()
    *
    * Show an arbitrary modal component with the given parameters.
    * Returns a promise that is resolved when the modal is closed and rejected when the modal is dismissed/cancelled.
    */
    async show<T extends BaseModal>(modalType: new(...args: any[]) => T, params?: { [P in keyof T]? : T[P] },
        options?: ModalOptions): Promise<any> {
        options = options || {};

        const dialogOptions: MatDialogConfig = {
          hasBackdrop: true,
          disableClose: options.closeOnBackdropClick === false,
          panelClass: this.addPanelClass(options.overlayClass, 'ngf-dialog-panel'),
          width: options.width
      };

      const modalRef = this.open(modalType, dialogOptions);

      // Add parameters to modal component
      Object.assign(modalRef.componentInstance, params);

        const modal: BaseModal = modalRef.componentInstance;

        // Close event - returns data from the modal
        modal.onClose.subscribe(closeResult => {
            // distinguish closing with undefined from cancelling (which can be closing with undefined)
            const result = closeResult === undefined ? null : closeResult;

            modalRef.close(result);
            modal.onClose.unsubscribe();
        });

        // Cancel event - returns no data
        modal.onCancel.subscribe(() => {

          // This method is causing a rejection of Promise with reason passed as argument
          modalRef.close(DialogService.CANCEL_MODAL);
          modal.onCancel.unsubscribe();
        });

        return lastValueFrom(modalRef.afterClosed()
        .pipe(
            map(result => {
                // if dismissed by backdrop click, or esc, result will be undefined
                if (result === undefined || result === DialogService.CANCEL_MODAL) {
                    throw DialogService.CANCEL_MODAL;
                }
                return result;
            })
        ))
            .catch(reason => {
                throw reason;
            });
    }
}
